import trackApiFetch from './track-api-fetch';
import vtRequestId from './vt-request-id';

const buildApiUrl = (path, host = process.env.NEXT_PUBLIC_VT_API_ENDPOINT) => `${host}${path}`;

export class ApiFetchError extends Error {
  body: string;

  constructor(message: string, public path: string, public response: Response, body: string) {
    super(message);
    this.response = response;
    this.path = path;
    this.body = body;
  }
}

const safeGetJson = async (res: Response) => res.json().catch(() => null);

interface ApiFetchOptions extends RequestInit {
  host?: string;
}

/**
 * A generic Fetcher for use with SWR that adapts to our API service.
 **/
export default function apiFetch<T = any>(path: string, { host, ...init }: ApiFetchOptions = {}) {
  const url = buildApiUrl(path, host);
  return fetch(url, {
    ...init,
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-vt-requestid': vtRequestId(),
      ...(init.headers || {}),
    },
  })
    .then(async (res) => {
      trackApiFetch('apiFetch', 'start', `${init.method || 'GET'} ${url}`);

      if (init.body) {
        trackApiFetch('apiFetch', 'body', `${vtRequestId()} ${init.body}`);
      }

      // we need to clone the response so that we can pass it into the error handler
      const body = await res.clone().text();
      const json = await safeGetJson(res);

      if (res.ok) {
        trackApiFetch(
          'apiFetch',
          'response_success',
          `${vtRequestId()} ${res?.status} ${res?.statusText} ${body}`
        );
        return json as T;
      }

      throw new ApiFetchError(json?.error || 'Error fetching data', path, res, body);
    })
    .catch(async (err: ApiFetchError) => {
      trackApiFetch(
        'apiFetch',
        'response_error',
        `${vtRequestId()} ${err.response.status} ${JSON.stringify(err)} ${err.body} `
      );
      throw err;
    });
}
